// Tiny helper to route Supabase Storage public-object URLs through
// Supabase's on-the-fly image transformer. Passes non-Supabase URLs
// through unchanged so callsites can use it for placeholder paths,
// external CDN URLs, etc., without branching.
//
// Format reference:
//   public original:    /storage/v1/object/public/<bucket>/<path>
//   transformed render: /storage/v1/render/image/public/<bucket>/<path>?width=…
//
// PageSpeed Insights flagged ~26MB of savings per property page from
// serving full-resolution JPGs at thumbnail / hero sizes. Setting
// width to roughly the displayed pixel size (with a 2× factor for
// retina via srcset where supported) plus format=webp drops most of
// that weight without touching the source files in storage.

export interface TransformOpts {
  /** Target display width in CSS pixels. The helper does not auto-
   *  scale for DPR, pass the largest size you actually render. */
  width?: number;
  height?: number;
  /** 20-100. Defaults to 80 (good visual quality, ~⅓ the size of the
   *  source JPG). */
  quality?: number;
  /** webp is the safe default in 2026; avif is smaller but Safari
   *  support is partial. origin keeps the source format. */
  format?: "webp" | "avif" | "origin";
  /** How Supabase scales the source into the target box. Defaults to
   *  cover (matches the typical object-cover CSS). */
  resize?: "cover" | "contain" | "fill";
}

const isSupabasePublic = (url: string): boolean =>
  url.includes("/storage/v1/object/public/");

/** Where storage objects are served from. Moving off Lovable Cloud changes
 *  this (a new Supabase project, later a Worker image route); stored refs
 *  don't have to. Mirrored by scripts/lib/image-url.mjs and
 *  supabase/functions/_shared/imageUrl.ts (src/test/imageUrlMirror.test.ts). */
const BACKEND_STORAGE = String(import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/+$/, "");

/**
 * On the live sites (and their workers.dev previews) photos come from the
 * site's own domain: worker/index.js serves /storage/v1/… for the public
 * buckets, so anything that can read a page can fetch its photos (agent
 * sandboxes often block *.supabase.co). Local dev and the prerender's local
 * server have no Worker, so they use the storage host; the prerender then
 * rewrites those URLs to the site's domain (scripts/lib/image-url.mjs#onSiteStorage).
 */
export const storageBaseFor = (location: { hostname: string; origin: string } | null | undefined, backend: string = BACKEND_STORAGE): string => {
  if (!location) return backend;
  const h = location.hostname;
  if (!h || h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h.endsWith(".local")) return backend;
  return location.origin;
};

const STORAGE_BASE = storageBaseFor(typeof window !== "undefined" ? window.location : null);

const STORAGE_URL = /^https?:\/\/[^/]+\/storage\/v1\/(?:object|render\/image)\/public\/([^?#]+)/;

/**
 * A stored image reference → the URL it is served from today. Accepts a
 * storage path ("property-images/p/<id>/<hash>.jpg"), or a legacy absolute
 * storage URL from any project host, which is rebuilt on the current base so
 * old rows survive a move to a new project. Site-relative paths and other
 * URLs (external, blob:, data:) come back unchanged.
 */
export const resolveStorageUrl = (ref: string | null | undefined, base: string = STORAGE_BASE): string => {
  if (!ref) return "";
  const root = base.replace(/\/+$/, "");
  const legacy = STORAGE_URL.exec(ref);
  if (legacy) return root ? `${root}/storage/v1/object/public/${legacy[1]}` : ref;
  if (/^[a-z][a-z0-9+.-]*:/i.test(ref) || ref.startsWith("/")) return ref;
  return root ? `${root}/storage/v1/object/public/${ref}` : ref;
};

export const transformImage = (
  ref: string | null | undefined,
  opts: TransformOpts = {},
): string => {
  const url = resolveStorageUrl(ref);
  if (!url) return "";
  if (!isSupabasePublic(url)) return url;

  const transformed = url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/",
  );
  const params = new URLSearchParams();
  if (opts.width != null) params.set("width", String(opts.width));
  if (opts.height != null) params.set("height", String(opts.height));
  params.set("quality", String(opts.quality ?? 80));
  params.set("format", opts.format ?? "webp");
  // Resize semantics, measured against the live render endpoint (a
  // 1920×1280 source):
  //   width=800 (no resize)          → 800×1280  ← width clamped, height
  //                                    KEPT, squashed/"zoomed" thumbnails
  //   width=800&resize=contain       → 800×533   ← proportional ✓
  //   width=800&height=533&resize=cover → 800×533 exact crop ✓
  // So a single dimension must ALWAYS ship an explicit resize=contain
  // (fit within a width×width box, aspect preserved, the consuming
  // <img>'s CSS handles framing). Both dimensions keep cover so the
  // server crops to the exact box the layout renders.
  if (opts.resize) {
    params.set("resize", opts.resize);
  } else if (opts.width != null && opts.height != null) {
    params.set("resize", "cover");
  } else if (opts.width != null || opts.height != null) {
    params.set("resize", "contain");
  }
  // Cache-buster. Bump when transform defaults change so browsers and
  // any intermediate CDN cache fetch a fresh render instead of serving
  // a previously broken (e.g. squashed 800×1280) variant.
  params.set("v", "5");
  return `${transformed}?${params.toString()}`;
};

/**
 * Build a srcset string for responsive <img>s. Pass an array of widths
 * (in CSS pixels); each entry becomes a `<url> <w>w` token. The
 * browser picks the best size based on the viewport + the `sizes`
 * attribute on the <img>.
 */
export const transformImageSrcSet = (
  url: string | null | undefined,
  widths: number[],
  opts: Omit<TransformOpts, "width"> = {},
): string => {
  if (!url || !isSupabasePublic(resolveStorageUrl(url))) return "";
  return widths
    .map((w) => `${transformImage(url, { ...opts, width: w })} ${w}w`)
    .join(", ");
};
